/* eslint-disable no-undef */
import spinnerHttpRequest from '@/api/spinner-http-request';
import baseHttpRequest from '@/api/base-http-request';
import {
    LoginWithEmailPasswordResponseT,
    LoginWithSMSCodeResponseT,
    MyInfoT,
    CompanyListByEmailResponseT,
    RegisterInfoResponseT,
    RegisterUserResponseT,
} from '@/types/user-types';

const verifyLoginWithEmailPassword = async (
    email: string,
    password: string,
) => {
    const response =
        await spinnerHttpRequest.post<LoginWithEmailPasswordResponseT>(
            '/auth/login',
            {
                uid: email,
                upw: password,
            },
        );

    return response.data;
};

const sendSMSCodeForLogin = async (phoneNumber: string, loginToken: string) => {
    const response = await spinnerHttpRequest.post<{}>('/certi/login-sms', {
        phoneNumber,
        loginToken,
    });

    return response.data;
};

const verifyLoginWithSMSCode = async (
    phoneNumber: string,
    loginToken: string,
    certiNumber: string,
) => {
    const response = await spinnerHttpRequest.post<LoginWithSMSCodeResponseT>(
        '/certi/login-confirm',
        {
            phoneNumber,
            loginToken,
            certiNumber,
        },
    );

    return response.data;
};

const sendSMSCodeForRegister = async (phoneNumber: string) => {
    const response = await spinnerHttpRequest.post<{}>('/certi/sms', {
        phoneNumber,
    });

    return response.data;
};

const verifyRegisterWithSMSCode = async (
    phoneNumber: string,
    certiNumber: string,
) => {
    const response = await spinnerHttpRequest.post<{}>('/certi/sms-confirm', {
        phoneNumber,
        certiNumber,
    });

    return response.data;
};

const fetchNewTokens = async (tokens: {
    accessToken: string;
    refreshToken: string;
}) => {
    const response = await spinnerHttpRequest.post<LoginWithSMSCodeResponseT>(
        '/auth/reissue',
        tokens,
    );

    return response.data;
};

const logout = async (tokens: {accessToken: string; refreshToken: string}) => {
    const response = await spinnerHttpRequest.post<{}>('/auth/logout', tokens);

    return response.data;
};

const fetchMyInfo = async () => {
    const response = await baseHttpRequest.get<MyInfoT>('/my');

    return response.data;
};

const downloadManual = async () => {
    const response = await spinnerHttpRequest.get<BlobPart>(
        '/files/business_product_intro_20200401.pdf',
        {responseType: 'blob'},
    );

    return response.data;
};

const sendEmailVerification = async (params: {
    receiverEmail: string;
    companyId?: number;
    companyName?: string;
}) => {
    const response = await spinnerHttpRequest.post<{
        key: string;
        timestamp: number;
    }>('/sign-up/auth/mail', params);

    return response.data;
};

/**
 * @description 이메일 인증 없이 회원가입 시, 이메일 검증 API (중복 여부 & 허용 도메인 여부 체크)
 * @see https://dev-business-web-api.withinapi.com/swagger-ui/index.html#/회원가입/validateEmail
 * @param email
 * @param companyId
 */
const validateEmail = async (email: string, companyId: number) => {
    await spinnerHttpRequest.get(
        `/sign-up/validate/mail?email=${email}&companyId=${companyId}`,
    );
};

const fetchCompanyListByEmail = async (email: string) => {
    const response = await spinnerHttpRequest.get<CompanyListByEmailResponseT>(
        `/companies/by-email?email=${email}`,
    );

    return response.data;
};

const fetchRegisterInfo = async (email: string, companyId: number) => {
    const response = await spinnerHttpRequest.get<RegisterInfoResponseT>(
        `/sign-up/info?email=${email}&companyId=${companyId}`,
    );

    return response.data;
};

const registerUser = async (params: {
    email: string;
    companyId: number;
    phoneNumber: string;
    password: string;
    nickname: string;
    name: string;
    gender: 'MALE' | 'FEMALE';
    birth: string;
    privacyAuxiliaryPolicy: boolean;
    marketingAcceptance: boolean;
    locationPolicy: boolean;
}) => {
    const response = await spinnerHttpRequest.post<RegisterUserResponseT>(
        '/sign-up',
        params,
    );

    return response.data;
};

const patchMyInfo = async (params: {name: string}) => {
    const response = await spinnerHttpRequest.patch<MyInfoT>('/my', params);

    return response.data;
};

export {
    verifyLoginWithEmailPassword,
    sendSMSCodeForLogin,
    verifyLoginWithSMSCode,
    fetchNewTokens,
    logout,
    fetchMyInfo,
    downloadManual,
    sendEmailVerification,
    validateEmail,
    fetchCompanyListByEmail,
    fetchRegisterInfo,
    registerUser,
    sendSMSCodeForRegister,
    verifyRegisterWithSMSCode,
    patchMyInfo,
};
